import type { AnyContentType } from '@/lib/types'

/**
 * Human-readable labels for each content type.
 */
const LABELS: Record<AnyContentType, string> = {
  facebook_post: 'Facebook Post',
  twitter_post: 'Twitter Post',
  instagram_post: 'Instagram Post',
  linkedin_post: 'LinkedIn Post',
  blog_seo: 'Blog / SEO',
  email_outreach: 'Email Outreach',
  facebook_ad: 'Facebook Ad',
  instagram_ad: 'Instagram Ad',
  google_ads: 'Google Ads',
  gbp_post: 'GBP Post',
  social_media: 'Social Media',
  ad_copy: 'Ad Copy',
}

/**
 * Return a human-readable label for a content type.
 * Falls back to the raw type string for unknown values.
 */
export function getContentTypeLabel(type: AnyContentType): string {
  return LABELS[type] ?? (type as string)
}

/**
 * Color mappings by semantic group:
 *   blue   — organic social / post types
 *   orange — paid ad types
 *   green  — SEO / blog content
 *   red    — email outreach
 *   grey   — legacy umbrella types (social_media, ad_copy)
 */
const COLORS: Record<AnyContentType, 'blue' | 'green' | 'red' | 'grey' | 'orange'> = {
  // Organic social posts → blue
  facebook_post: 'blue',
  twitter_post: 'blue',
  instagram_post: 'blue',
  linkedin_post: 'blue',
  gbp_post: 'blue',
  // Paid ads → orange
  facebook_ad: 'orange',
  instagram_ad: 'orange',
  google_ads: 'orange',
  // Blog / SEO → green
  blog_seo: 'green',
  // Email → red
  email_outreach: 'red',
  // Legacy catch-all types → grey
  social_media: 'grey',
  ad_copy: 'grey',
}

/**
 * Return a semantic color for a content type badge / tag.
 */
export function getContentTypeColor(type: AnyContentType): 'blue' | 'green' | 'red' | 'grey' | 'orange' {
  return COLORS[type] ?? 'grey'
}

const PLATFORMS: Partial<Record<AnyContentType, string>> = {
  facebook_post: 'facebook',
  facebook_ad: 'facebook',
  social_media: 'facebook',
  ad_copy: 'facebook',
  twitter_post: 'twitter',
  instagram_post: 'instagram',
  instagram_ad: 'instagram',
  linkedin_post: 'linkedin',
  blog_seo: 'medium',
  email_outreach: 'gmail',
  google_ads: 'google',
  gbp_post: 'google',
}

/**
 * Return the platform key for a content type (used for brand icon lookup).
 * Returns null for types with no clear single platform.
 */
export function getContentTypePlatform(type: AnyContentType): string | null {
  return PLATFORMS[type] ?? null
}

/**
 * Resolve a human-readable title for a queue item.
 * Priority: subject (emails) → seoMetadata.metaTitle (blog) → first sentence of content body.
 * Handles both camelCase (from GraphQL) and snake_case (legacy) field names.
 */
export function getItemTitle(item: {
  contentType?: string | null
  content_type?: string | null
  subject?: string | null
  seoMetadata?: { metaTitle?: string | null; meta_title?: string | null } | null
  seo_metadata?: { meta_title?: string | null; metaTitle?: string | null } | null
  content?: string | null
}): string {
  const ct = item.contentType ?? item.content_type ?? ''
  if (ct.includes('email') || ct.includes('outreach')) {
    const subject = item.subject
    if (subject) return subject
  }
  const metaTitle =
    item.seoMetadata?.metaTitle ??
    item.seoMetadata?.meta_title ??
    item.seo_metadata?.meta_title ??
    item.seo_metadata?.metaTitle
  if (metaTitle) return metaTitle
  const raw = item.content ?? ''
  // If content is JSON, try to extract `.content` for first-sentence preview
  try {
    const parsed = JSON.parse(raw) as { content?: unknown }
    if (parsed && typeof parsed.content === 'string') {
      const firstSentence = parsed.content.split(/[.!?\n]/)[0].trim().slice(0, 80)
      return firstSentence || raw.slice(0, 80) || '—'
    }
  } catch {
    // not JSON — fall through and use raw text
  }
  const firstSentence = raw.split(/[.!?\n]/)[0].trim().slice(0, 80)
  return firstSentence || '—'
}
